import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Area
} from "recharts";
import { Users, DollarSign, Briefcase, TrendingUp, PieChart as PieIcon, Percent, CreditCard, AlertTriangle } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { useTimezone } from "../../contexts/TimezoneContext";

const AnalyticsPage = () => {
  const { isDark } = useTheme();
  const { timezone, formatDate, getTimezoneLabel } = useTimezone();
  const [analytics, setAnalytics] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const COLORS = ["#0046BE", "#FFCC00", "#00D1FF", "#10B981", "#8B5CF6", "#EF4444"];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };

      const [analyticsRes, profitRes, reconcileRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, config).catch(e => ({ data: null, error: e })),
        axios.get(`${API}/analytics/profit-summary`, config).catch(e => ({ data: null, error: e })),
        axios.get(`${API}/analytics/payment-reconciliation`, config).catch(e => ({ data: null, error: e }))
      ]);

      // Set data from successful requests
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
      if (profitRes.data) setProfitData(profitRes.data);
      if (reconcileRes.data) setReconciliationData(reconcileRes.data);
      
      // Only show error if all requests failed (and not auth errors)
      if (analyticsRes.error && profitRes.error && reconcileRes.error) {
        if (analyticsRes.error.response?.status !== 401) {
          console.error("Analytics fetch error:", analyticsRes.error);
          toast.error("Failed to load analytics");
        }
      }
    } catch (error) {
      console.error("Unexpected analytics error:", error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: "Total Revenue",
      value: `$${(analytics?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: "text-green-400",
      bgColor: "bg-green-500/10"
    },
    {
      title: "Net Profit",
      value: `$${(profitData?.summary?.net_profit || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: "text-[#FFCC00]",
      bgColor: "bg-[#FFCC00]/10"
    },
    {
      title: "Jobs Completed",
      value: analytics?.completed_jobs || 0,
      icon: Briefcase,
      color: "text-blue-400",
      bgColor: "bg-blue-500/10"
    },
    {
      title: "Profit Margin",
      value: `${profitData?.summary?.profit_margin || 0}%`,
      icon: Percent,
      color: "text-purple-400",
      bgColor: "bg-purple-500/10"
    }
  ];

  const serviceData = analytics?.jobs_by_service
    ? Object.entries(analytics.jobs_by_service).map(([name, value]) => ({
        name: name.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()),
        value
      }))
    : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-xl">
          <p className="text-muted-foreground text-sm">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-white font-semibold" style={{ color: p.color }}>
              {p.name}: ${(p.value || 0).toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Analytics</h2>
        <p className="text-muted-foreground">Track your business performance and insights</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <Card key={index} className="glass-card border-theme" data-testid={`analytics-stat-${index}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                  <h3 className="text-2xl font-bold text-foreground">{stat.value}</h3>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs for different analytics views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-theme-surface border border-theme p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground">
            Overview
          </TabsTrigger>
          <TabsTrigger value="profit" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground">
            Profit Analysis
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-[#0046BE] data-[state=active]:text-white text-muted-foreground">
            Payment Reconciliation
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Revenue Chart */}
            <Card className="glass-card border-theme" data-testid="revenue-chart">
              <CardHeader>
                <CardTitle className="text-foreground font-['Manrope']">Monthly Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.monthly_revenue && analytics.monthly_revenue.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={analytics.monthly_revenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                      <XAxis dataKey="month" stroke="#ffffff60" fontSize={12} />
                      <YAxis stroke="#ffffff60" fontSize={12} tickFormatter={(value) => `$${value}`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill="#FFCC00" radius={[4, 4, 0, 0]} name="Revenue" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No revenue data available yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Jobs by Service Type */}
            <Card className="glass-card border-theme" data-testid="jobs-by-service-chart">
              <CardHeader>
                <CardTitle className="text-foreground font-['Manrope']">Jobs by Service Type</CardTitle>
              </CardHeader>
              <CardContent>
                {serviceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={serviceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {serviceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#0a0a12',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No jobs data available yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="glass-card border-theme" data-testid="conversion-rate-card">
              <CardContent className="p-6">
                <h4 className="text-muted-foreground text-sm mb-2">Job Completion Rate</h4>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-foreground">
                    {analytics?.total_jobs > 0
                      ? Math.round((analytics.completed_jobs / analytics.total_jobs) * 100)
                      : 0}%
                  </span>
                  <span className="text-green-400 text-sm mb-1">
                    {analytics?.completed_jobs || 0} of {analytics?.total_jobs || 0} jobs
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-theme" data-testid="avg-job-value-card">
              <CardContent className="p-6">
                <h4 className="text-muted-foreground text-sm mb-2">Average Job Value</h4>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-foreground">
                    ${analytics?.completed_jobs > 0
                      ? Math.round(analytics.total_revenue / analytics.completed_jobs)
                      : 0}
                  </span>
                  <span className="text-muted-foreground text-sm mb-1">per completed job</span>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-theme" data-testid="pending-jobs-card">
              <CardContent className="p-6">
                <h4 className="text-muted-foreground text-sm mb-2">Pending Jobs</h4>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold text-foreground">{analytics?.pending_jobs || 0}</span>
                  <span className="text-[#FFCC00] text-sm mb-1">scheduled jobs waiting</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Profit Analysis Tab */}
        <TabsContent value="profit" className="space-y-6 mt-6">
          {profitData && (
            <>
              {/* Profit Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Total Revenue</p>
                    <p className="text-xl font-bold text-foreground">${profitData.summary.total_revenue.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Parts Cost</p>
                    <p className="text-xl font-bold text-red-400">-${profitData.summary.company_parts_cost.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Commissions Paid</p>
                    <p className="text-xl font-bold text-orange-400">-${profitData.summary.total_commission.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme border-[#FFCC00]/30">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Net Profit</p>
                    <p className="text-xl font-bold text-[#FFCC00]">${profitData.summary.net_profit.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Profit Margin</p>
                    <p className="text-xl font-bold text-green-400">{profitData.summary.profit_margin}%</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Revenue vs Profit by Month */}
                <Card className="glass-card border-theme">
                  <CardHeader>
                    <CardTitle className="text-foreground font-['Manrope']">Revenue vs Profit by Month</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profitData.by_month.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={profitData.by_month}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                          <XAxis dataKey="month" stroke="#ffffff60" fontSize={12} />
                          <YAxis stroke="#ffffff60" fontSize={12} tickFormatter={(v) => `$${v}`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar dataKey="revenue" fill="#0046BE" name="Revenue" radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="profit" stroke="#FFCC00" strokeWidth={3} name="Profit" dot={{ fill: '#FFCC00' }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                        No monthly data available yet
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Profit by Technician */}
                <Card className="glass-card border-theme">
                  <CardHeader>
                    <CardTitle className="text-foreground font-['Manrope']">Performance by Technician</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {profitData.by_technician.length > 0 ? (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {profitData.by_technician.map((tech, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface">
                            <div>
                              <p className="text-white font-medium">{tech.name}</p>
                              <p className="text-muted-foreground text-sm">{tech.jobs} jobs completed</p>
                            </div>
                            <div className="text-right">
                              <p className="text-white font-semibold">${tech.revenue.toLocaleString()}</p>
                              <p className="text-[#FFCC00] text-sm">Profit: ${tech.profit.toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                        No technician data available yet
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Profit by Job Source */}
              {profitData.by_source.length > 0 && (
                <Card className="glass-card border-theme">
                  <CardHeader>
                    <CardTitle className="text-foreground font-['Manrope']">Revenue by Job Source</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={profitData.by_source} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                        <XAxis type="number" stroke="#ffffff60" tickFormatter={(v) => `$${v}`} />
                        <YAxis type="category" dataKey="name" stroke="#ffffff60" width={120} fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="revenue" fill="#0046BE" name="Revenue" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Payment Reconciliation Tab */}
        <TabsContent value="payments" className="space-y-6 mt-6">
          {reconciliationData && (
            <>
              {/* Reconciliation Summary */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Expected Payments</p>
                    <p className="text-xl font-bold text-foreground">${reconciliationData.summary.total_expected.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Received Payments</p>
                    <p className="text-xl font-bold text-green-400">${reconciliationData.summary.total_received.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className={`glass-card ${reconciliationData.summary.difference > 0 ? 'border-red-500/30' : 'border-green-500/30'}`}>
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Outstanding Balance</p>
                    <p className={`text-xl font-bold ${reconciliationData.summary.difference > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      ${reconciliationData.summary.difference.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card className="glass-card border-theme">
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-xs mb-1">Reconciliation Rate</p>
                    <p className="text-xl font-bold text-[#FFCC00]">{reconciliationData.summary.reconciliation_rate}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Status Breakdown */}
              <div className="grid md:grid-cols-3 gap-4">
                <Card className="glass-card border-green-500/20">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Fully Paid Jobs</p>
                      <p className="text-2xl font-bold text-green-400">{reconciliationData.summary.fully_paid}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card border-yellow-500/20">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Partially Paid</p>
                      <p className="text-2xl font-bold text-yellow-400">{reconciliationData.summary.partially_paid}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass-card border-red-500/20">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-red-400" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-sm">Unpaid Jobs</p>
                      <p className="text-2xl font-bold text-red-400">{reconciliationData.summary.unpaid}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Payments by Type */}
                <Card className="glass-card border-theme">
                  <CardHeader>
                    <CardTitle className="text-foreground font-['Manrope']">Payments by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reconciliationData.by_payment_type.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={reconciliationData.by_payment_type}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={3}
                            dataKey="amount"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {reconciliationData.by_payment_type.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => `$${value.toLocaleString()}`}
                            contentStyle={{
                              backgroundColor: '#0a0a12',
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                        No payment data available yet
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Unreconciled Jobs List */}
                <Card className="glass-card border-theme">
                  <CardHeader>
                    <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      Unreconciled Jobs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {reconciliationData.unreconciled_jobs.length > 0 ? (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {reconciliationData.unreconciled_jobs.map((job, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-theme-surface border-l-2 border-red-500/50">
                            <div>
                              <p className="text-white font-medium text-sm">{job.title}</p>
                              <p className="text-muted-foreground text-xs">{job.customer_name}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-foreground text-sm">Charged: ${job.total_charged}</p>
                              <p className="text-red-400 text-xs">Owed: ${job.difference}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-green-400">
                        ✓ All jobs are fully reconciled!
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
